export interface AsyncMutex {
  runExclusive<T>(fn: () => Promise<T>, opts?: { label?: string }): Promise<T>;
  isLocked(): boolean;
  pending(): number;
}

export interface AsyncMutexOptions {
  timeoutMs?: number;
  onTimeout?: (label: string, ms: number) => void;
  debug?: (event: "acquire" | "release" | "timeout" | "error", label: string, ms: number) => void;
}

class TimeoutError extends Error {
  constructor(label: string, ms: number) {
    super(`[async-mutex] timeout after ${ms}ms (label=${label})`);
    this.name = "AsyncMutexTimeoutError";
  }
}

export function createAsyncMutex(opts: AsyncMutexOptions = {}): AsyncMutex {
  const timeoutMs = opts.timeoutMs ?? 0;
  let tail: Promise<unknown> = Promise.resolve();
  let pendingCount = 0;
  let locked = false;

  const runExclusive = <T,>(fn: () => Promise<T>, runOpts?: { label?: string }): Promise<T> => {
    const label = runOpts?.label || "anon";
    pendingCount += 1;

    // Conforme o plano (step 3 de printer-mutex.md): no timeout, soltar o
    // mutex, logar warning e seguir pra próxima. Trade-off explícito:
    // se a operação subjacente (ex.: transferOut USB) eventualmente
    // terminar depois do timeout, há uma janela curta em que a próxima
    // operação pode rodar concorrentemente. Essa é a escolha de liveness
    // versus correção máxima — sem isso, uma impressora travada
    // bloquearia toda a fila por minutos.
    //
    // Mitigação no caller: sendRaw, ao receber timeout do mutex, marca
    // partial=true pra não re-enfileirar e duplicar cupom.
    const work = tail.then(async () => {
      locked = true;
      const start = Date.now();
      opts.debug?.("acquire", label, 0);
      try {
        if (timeoutMs > 0) {
          let timer: ReturnType<typeof setTimeout> | null = null;
          const timeoutPromise = new Promise<never>((_, reject) => {
            timer = setTimeout(() => {
              const err = new TimeoutError(label, timeoutMs);
              opts.onTimeout?.(label, timeoutMs);
              opts.debug?.("timeout", label, timeoutMs);
              reject(err);
            }, timeoutMs);
          });
          try {
            return (await Promise.race([fn(), timeoutPromise])) as T;
          } finally {
            if (timer) clearTimeout(timer);
          }
        }
        return await fn();
      } finally {
        const elapsed = Date.now() - start;
        opts.debug?.("release", label, elapsed);
        locked = false;
      }
    });

    // `.catch(() => undefined)` impede que rejeições propaguem para o
    // `tail`, o que travaria toda a fila numa única falha.
    tail = work.catch(() => undefined);

    return work.finally(() => {
      pendingCount -= 1;
    }) as Promise<T>;
  };

  return {
    runExclusive,
    isLocked: () => locked,
    pending: () => pendingCount,
  };
}
